import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { AlertTriangle, Clock, Edit3, ChevronRight } from 'lucide-react';
import BrandSelect from '../../../Common/BrandSelect';
import '../../../Common/ll-modal.css';
import './invoice-time-import.css';

const GROUP_BY_OPTIONS = [
  { value: 'by_project', label: 'Per projekt' },
  { value: 'by_person', label: 'Per person' },
  { value: 'by_entry', label: 'Per post' },
];

const fmtKr = (n) =>
  new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Invoicing convention: bill last completed month after close. Default the
// range to the previous calendar month so users land on the right window
// without fiddling.
const lastFullMonthRange = () => {
  const now = new Date();
  const firstOfThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayLastMonth = new Date(firstOfThis.getTime() - 86400000);
  const firstOfLast = new Date(lastDayLastMonth.getFullYear(), lastDayLastMonth.getMonth(), 1);
  return { from: ymd(firstOfLast), to: ymd(lastDayLastMonth) };
};

/**
 * InvoiceTimeImportModal — focused modal for picking billable time entries
 * to push onto a customer invoice.
 *
 * Styled by invoice-time-import.css (.inv-ti-modal). Flow: pick a date range +
 * grouping → preview loads itself → check rows + edit descriptions → commit.
 *
 * Props:
 *   show, onClose
 *   previewData: { groups: [...], totals: { amountExclVat } }
 *   loading: preview load in flight
 *   error: string | null
 *   importing: confirm in flight
 *   onLoadPreview: ({ fromDate, toDate, groupBy }) => void
 *   onConfirmImport: ({ fromDate, toDate, groupBy, selectedGroups }) => void
 */
const InvoiceTimeImportModal = ({
  show,
  onClose,
  previewData,
  loading,
  error,
  importing,
  onLoadPreview,
  onConfirmImport,
}) => {
  const initialRange = useRef(lastFullMonthRange()).current;
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [groupBy, setGroupBy] = useState('by_project');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [descriptions, setDescriptions] = useState({});

  // Auto-select all groups on each fresh preview load
  useEffect(() => {
    if (previewData?.groups) {
      setSelectedKeys(new Set(previewData.groups.map((g) => g.groupKey)));
      setDescriptions({});
    }
  }, [previewData]);

  // Auto-load preview when the modal opens, and re-fetch (debounced) when
  // any control changes while the modal is visible. Keeps the latest
  // onLoadPreview in a ref so this effect's deps stay control-only.
  const onLoadPreviewRef = useRef(onLoadPreview);
  useEffect(() => {
    onLoadPreviewRef.current = onLoadPreview;
  }, [onLoadPreview]);

  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => {
      onLoadPreviewRef.current({ fromDate, toDate, groupBy });
    }, 250);
    return () => clearTimeout(t);
  }, [show, fromDate, toDate, groupBy]);

  const groups = previewData?.groups || [];
  const allSelected = groups.length > 0 && groups.every((g) => selectedKeys.has(g.groupKey));
  const totalAmount = groups
    .filter((g) => selectedKeys.has(g.groupKey))
    .reduce((acc, g) => acc + (Number(g.totalAmount) || 0), 0);

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(groups.map((g) => g.groupKey)));
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedGroups = groups
      .filter((g) => selectedKeys.has(g.groupKey))
      .map((g) => ({
        groupKey: g.groupKey,
        description: descriptions[g.groupKey] || undefined,
      }));
    onConfirmImport({ fromDate, toDate, groupBy, selectedGroups });
  };

  const handleClose = () => {
    if (importing) return;
    setSelectedKeys(new Set());
    setDescriptions({});
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered className="ll-modal inv-ti-modal">
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Importera från tidrapport</Modal.Title>
          <p className="ll-modal__subtitle">Godkänd, ofakturerad tid för kunden läggs till som fakturarader.</p>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="ti-controls">
          <div className="field">
            <label htmlFor="ti-from">Från</label>
            <input
              id="ti-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ti-to">Till</label>
            <input
              id="ti-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="field field--group">
            <label htmlFor="ti-group">Gruppera</label>
            <BrandSelect
              inputId="ti-group"
              name="groupBy"
              options={GROUP_BY_OPTIONS}
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            />
          </div>
          {loading && (
            <div className="ti-controls-spinner" aria-live="polite">
              <Spinner animation="border" size="sm" />
              <span>Laddar…</span>
            </div>
          )}
        </div>

        {error && (
          <div className="ti-error" role="alert">
            <AlertTriangle size={15} />
            <span>{error}</span>
          </div>
        )}

        {previewData && groups.length > 0 && (
          <div className="ti-table-wrap">
            <table className="ti-rows-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Markera alla grupper"
                    />
                  </th>
                  <th>Beskrivning</th>
                  <th className="num">Antal</th>
                  <th className="num">Pris/st</th>
                  <th className="center">Moms</th>
                  <th className="num">Belopp</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const isExpanded = expandedKeys.has(g.groupKey);
                  const isSelected = selectedKeys.has(g.groupKey);
                  const entries = g.entries || [];
                  const hasEntries = entries.length > 0;
                  const rowClass = [isExpanded ? 'is-expanded' : '', isSelected ? '' : 'is-unselected']
                    .filter(Boolean).join(' ');
                  return (
                    <React.Fragment key={g.groupKey}>
                      <tr className={rowClass || undefined}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(g.groupKey)}
                            aria-label={`Markera ${g.label}`}
                          />
                        </td>
                        <td>
                          <div className="ti-desc-wrap">
                            {hasEntries && (
                              <button
                                type="button"
                                className={`ti-expand-toggle${isExpanded ? ' is-open' : ''}`}
                                onClick={() => toggleExpand(g.groupKey)}
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? 'Dölj poster' : 'Visa poster'}
                                title={`${entries.length} ${entries.length === 1 ? 'post' : 'poster'}`}
                              >
                                <ChevronRight size={14} />
                              </button>
                            )}
                            <input
                              type="text"
                              className="ti-desc-input"
                              value={descriptions[g.groupKey] ?? g.label}
                              onChange={(e) =>
                                setDescriptions((prev) => ({ ...prev, [g.groupKey]: e.target.value }))
                              }
                              aria-label={`Beskrivning för ${g.label}`}
                            />
                            <Edit3 size={13} className="ti-desc-icon" aria-hidden="true" />
                          </div>
                        </td>
                        <td className="num">{g.totalHours} h</td>
                        <td className="num">{fmtKr(g.billingRate)} kr</td>
                        <td className="center">{g.taxRate}</td>
                        <td className="num amount">{fmtKr(g.totalAmount)} kr</td>
                      </tr>
                      {isExpanded && hasEntries && (
                        <tr className="ti-detail-row">
                          <td></td>
                          <td colSpan={5}>
                            <div className="ti-entries-list" role="list">
                              {entries.map((e) => (
                                <div key={e.entryId} className="ti-entry-row" role="listitem">
                                  <span className="ti-entry-date">{e.date}</span>
                                  <span className="ti-entry-emp">{e.employeeName || ''}</span>
                                  <span className="ti-entry-desc" title={e.description || ''}>
                                    {e.description || '—'}
                                  </span>
                                  <span className="ti-entry-hours">{e.hours} h</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>Totalt valt (exkl. moms)</td>
                  <td className="num">{fmtKr(totalAmount)} kr</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {!loading && previewData && groups.length === 0 && (
          <div className="ti-empty">
            <Clock size={28} strokeWidth={1.5} />
            <h4>Inga fakturerbara tidposter</h4>
            <p>
              Det finns inga godkända, ofakturerade tidposter för denna kund i den valda
              perioden. Justera datumintervallet eller godkänn tidrapporter först.
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <span className="ll-modal__note">
          {previewData && groups.length > 0
            ? `${selectedKeys.size} av ${groups.length} valda`
            : ''}
        </span>
        <div className="ll-modal__actions">
          <button
            type="button"
            className="ll-btn ll-btn--secondary"
            onClick={handleClose}
            disabled={importing}
          >
            Avbryt
          </button>
          <button
            type="button"
            className="ll-btn ll-btn--primary"
            onClick={handleConfirm}
            disabled={importing || loading || groups.length === 0 || selectedKeys.size === 0}
          >
            {importing && <Spinner animation="border" size="sm" />}
            Lägg till på faktura
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceTimeImportModal;
